// ファイル名: kitchen-gift-categories.js
// キッチンギフトカテゴリセクション - ES6準拠インタラクション機能
// jQuery非依存、パフォーマンス最適化、アクセシビリティ対応

class KitchenGiftCategories {
  constructor() {
    this.section = document.querySelector('.kitchen-gift-section');
    this.cards = [];
    this.observer = null;
    this.resizeObserver = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    // 初期化実行
    this.init();
  }

  /**
   * 初期化メソッド
   */
  init() {
    if (!this.section) return;
    
    // DOM要素の取得
    this.cards = Array.from(this.section.querySelectorAll('.kitchen-gift-card'));
    
    // 機能の初期化
    this.setupIntersectionObserver();
    this.setupResizeObserver();
    this.setupCardInteractions();
    this.setupKeyboardNavigation();
    this.setupReducedMotionSupport();
    
    // 初期レスポンシブ調整
    this.adjustResponsiveLayout();
    
    console.log('Kitchen Gift Categories initialized');
  }

  /**
   * Intersection Observerの設定（スクロールアニメーション）
   */
  setupIntersectionObserver() {
    if (this.reducedMotion) return;

    const options = {
      root: null,
      rootMargin: '-10% 0px -10% 0px',
      threshold: [0, 0.1, 0.5, 1.0]
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const target = entry.target;
        const ratio = entry.intersectionRatio;
        
        if (entry.isIntersecting) {
          // 要素が表示された時のアニメーション
          this.animateElementIn(target, ratio);
        } else {
          // 要素が非表示になった時の処理
          this.animateElementOut(target);
        }
      });
    }, options);

    // カードを監視対象に追加
    this.cards.forEach((card, index) => {
      // 初期状態設定
      card.style.opacity = '0';
      card.style.transform = 'translateY(30px)';
      card.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
      card.style.transitionDelay = `${index * 0.1}s`;
      
      this.observer.observe(card);
    });

    // ヘッダーとボタンも監視
    const header = this.section.querySelector('.kitchen-gift-header');
    const footer = this.section.querySelector('.kitchen-gift-footer');
    
    if (header) this.observer.observe(header);
    if (footer) this.observer.observe(footer);
  }

  /**
   * 要素表示アニメーション
   */
  animateElementIn(element, ratio) {
    const delay = element.classList.contains('kitchen-gift-card') ? 
      Array.from(this.cards).indexOf(element) * 100 : 0;
    
    setTimeout(() => {
      element.style.opacity = '1';
      element.style.transform = 'translateY(0)';
      element.setAttribute('data-animated', 'true');
    }, delay);
  }

  /**
   * 要素非表示アニメーション
   */
  animateElementOut(element) {
    // 再利用可能性のため、完全には非表示にしない
    if (!this.reducedMotion && element.getAttribute('data-animated') === 'true') {
      element.style.opacity = '0.3';
      element.style.transform = 'translateY(10px)';
    }
  }

  /**
   * Resize Observerの設定（レスポンシブ対応）
   */
  setupResizeObserver() {
    if (!window.ResizeObserver) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      // デバウンス処理
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.adjustResponsiveLayout();
      }, 100);
    });

    this.resizeObserver.observe(this.section);
  }

  /**
   * カードインタラクションの設定
   */
  setupCardInteractions() {
    this.cards.forEach((card) => {
      const link = card.querySelector('.kitchen-gift-card-link');
      
      if (!link) return;

      // ホバーエフェクト
      card.addEventListener('mouseenter', (e) => this.handleCardHover(e, true));
      card.addEventListener('mouseleave', (e) => this.handleCardHover(e, false));
      
      // クリック処理
      card.addEventListener('click', (e) => this.handleCardClick(e));
      
      // フォーカス処理
      link.addEventListener('focus', (e) => this.handleCardFocus(e, true));
      link.addEventListener('blur', (e) => this.handleCardFocus(e, false));
      
      // タッチデバイス対応
      card.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
      card.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
    });
  }

  /**
   * カードホバー処理
   */
  handleCardHover(event, isHovering) {
    const card = event.currentTarget;
    const cardInner = card.querySelector('.kitchen-gift-card-inner');
    
    if (!cardInner || this.reducedMotion) return;

    if (isHovering) {
      cardInner.style.transform = 'translateY(-4px) scale(1.02)';
      card.style.zIndex = '10';
    } else {
      cardInner.style.transform = 'translateY(0) scale(1)';
      card.style.zIndex = 'auto';
    }
  }

  /**
   * カードクリック処理
   */
  handleCardClick(event) {
    const card = event.currentTarget;
    const link = card.querySelector('.kitchen-gift-card-link');
    
    if (!link) return;

    // アナリティクス追跡（必要に応じて）
    this.trackCardClick(card);
    
    // 外部リンクの場合の処理
    if (link.href && link.href.startsWith('http') && !link.href.includes(window.location.hostname)) {
      event.preventDefault();
      window.open(link.href, '_blank', 'noopener,noreferrer');
    }
  }

  /**
   * カードフォーカス処理
   */
  handleCardFocus(event, isFocused) {
    const card = event.currentTarget.closest('.kitchen-gift-card');
    
    if (!card) return;

    if (isFocused) {
      card.style.outline = '2px solid #c7a96b';
      card.style.outlineOffset = '2px';
      this.announceToScreenReader(card);
    } else {
      card.style.outline = '';
      card.style.outlineOffset = '';
    }
  }

  /**
   * タッチ開始処理
   */
  handleTouchStart(event) {
    const card = event.currentTarget;
    card.classList.add('touch-active');
    
    // ハプティックフィードバック（対応デバイスのみ）
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
  }

  /**
   * タッチ終了処理
   */
  handleTouchEnd(event) {
    const card = event.currentTarget;
    setTimeout(() => {
      card.classList.remove('touch-active');
    }, 150);
  }

  /**
   * キーボードナビゲーション設定
   */
  setupKeyboardNavigation() {
    this.section.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        this.handleArrowNavigation(event);
      }
    });
  }

  /**
   * 矢印キーナビゲーション処理
   */
  handleArrowNavigation(event) {
    const currentElement = document.activeElement;
    const currentCard = currentElement.closest('.kitchen-gift-card');
    
    if (!currentCard) return;

    event.preventDefault();
    
    const currentIndex = this.cards.indexOf(currentCard);
    let nextIndex;

    if (event.key === 'ArrowLeft') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : this.cards.length - 1;
    } else {
      nextIndex = currentIndex < this.cards.length - 1 ? currentIndex + 1 : 0;
    }

    const nextCard = this.cards[nextIndex];
    const nextLink = nextCard.querySelector('.kitchen-gift-card-link');
    
    if (nextLink) {
      nextLink.focus();
    }
  }

  /**
   * 動画酔い軽減対応
   */
  setupReducedMotionSupport() {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    mediaQuery.addEventListener('change', (e) => {
      this.reducedMotion = e.matches;
      
      if (this.reducedMotion) {
        // アニメーションを無効化
        this.cards.forEach(card => {
          card.style.transition = 'none';
          card.style.opacity = '1';
          card.style.transform = 'none';
        });
      }
    });
  }

  /**
   * レスポンシブレイアウト調整
   */
  adjustResponsiveLayout() {
    const viewport = this.getViewportSize();
    const grid = this.section.querySelector('.kitchen-gift-grid');
    
    if (!grid) return;

    // ビューポートに応じてカラム数を調整
    let columns;
    if (viewport.width <= 375) {
      columns = 1;
    } else if (viewport.width <= 767) {
      columns = 2;
    } else if (viewport.width <= 1023) {
      columns = 3;
    } else {
      columns = parseInt(getComputedStyle(grid).getPropertyValue('--columns-desktop')) || 4;
    }

    grid.style.setProperty('--current-columns', columns);
    
    // カードの高さ調整
    this.adjustCardHeights(viewport);
  }

  /**
   * カードの高さ調整
   */
  adjustCardHeights(viewport) {
    const baseHeight = viewport.width <= 767 ? 240 : 280;
    
    this.cards.forEach(card => {
      const content = card.querySelector('.kitchen-gift-card-content');
      if (content) {
        const contentHeight = content.scrollHeight;
        const recommendedHeight = Math.max(baseHeight, contentHeight + 80);
        card.style.minHeight = `${recommendedHeight}px`;
      }
    });
  }

  /**
   * ビューポートサイズ取得
   */
  getViewportSize() {
    return {
      width: window.innerWidth || document.documentElement.clientWidth,
      height: window.innerHeight || document.documentElement.clientHeight
    };
  }

  /**
   * スクリーンリーダー向けアナウンス
   */
  announceToScreenReader(card) {
    const price = card.querySelector('.kitchen-gift-price')?.textContent || '';
    const description = card.querySelector('.kitchen-gift-description')?.textContent || '';
    const message = `${price} ${description}のギフトカテゴリ`;
    
    // aria-live regionに一時的にメッセージを追加
    this.createAriaLiveAnnouncement(message);
  }

  /**
   * aria-live領域作成
   */
  createAriaLiveAnnouncement(message) {
    let liveRegion = document.getElementById('kg-live-region');
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'kg-live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.cssText = `
        position: absolute;
        left: -10000px;
        width: 1px;
        height: 1px;
        overflow: hidden;
      `;
      document.body.appendChild(liveRegion);
    }
    
    liveRegion.textContent = message;
    
    // 3秒後にクリア
    setTimeout(() => {
      liveRegion.textContent = '';
    }, 3000);
  }

  /**
   * アナリティクス追跡
   */
  trackCardClick(card) {
    const price = card.querySelector('.kitchen-gift-price')?.textContent || '';
    const description = card.querySelector('.kitchen-gift-description')?.textContent || '';
    
    // Google Analytics 4対応
    if (typeof gtag !== 'undefined') {
      gtag('event', 'kitchen_gift_card_click', {
        'category': 'Kitchen Gift',
        'label': `${price} - ${description}`,
        'value': 1
      });
    }
    
    // Shopify Analytics対応
    if (typeof analytics !== 'undefined' && analytics.track) {
      analytics.track('Kitchen Gift Card Clicked', {
        price_range: price,
        description: description,
        section: 'kitchen-gift-categories'
      });
    }
  }

  /**
   * クリーンアップ（必要に応じて呼び出し）
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    
    clearTimeout(this.resizeTimeout);
    
    // イベントリスナーの削除
    this.cards.forEach(card => {
      card.replaceWith(card.cloneNode(true));
    });
  }
}

// DOMContentLoaded後に初期化
document.addEventListener('DOMContentLoaded', () => {
  new KitchenGiftCategories();
});

// ページが既に読み込まれている場合の対応
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new KitchenGiftCategories();
  });
} else {
  new KitchenGiftCategories();
}

// Shopify Section Groupsでの動的読み込み対応
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId.includes('kitchen-gift')) {
    new KitchenGiftCategories();
  }
});

// ホットリロード対応（開発環境）
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:reorder', () => {
    setTimeout(() => {
      new KitchenGiftCategories();
    }, 100);
  });
}